import { computed, reactive, ref, unref, nextTick, watch, type ComputedRef } from 'vue';
import { isApiError } from '@/api/utilities/provider';
import {
	type ApiErrorResponse,
	type ListResponseMeta,
	type ListResponse,
	type PaginationType,
} from '@teamworksdev/influencer-core';
import type { PageProvider } from 'vue-virtual-scroll-grid/dist/pipeline';

type ServiceFunction = (...args: any[]) => Record<string, (...args: any[]) => Promise<any>>;

interface UseVirtualizationOptions {
	service: ServiceFunction;
	requestKey: string;
	params: ComputedRef<{ [key: string]: any }>;
	args?: ComputedRef<any[]>;
	paginationType?: PaginationType;
	paginationStartPage?: number;
}

export function useVirtualization<I>(options: UseVirtualizationOptions) {
	const {
		service,
		requestKey,
		params,
		args = computed(() => []),
		paginationType = 'standard',
		paginationStartPage = 1,
	} = options;
	type GetListResponse = ListResponse<any>;
	const isItemsLoading = ref(false);
	const items = ref<I[]>();
	const allItems = ref<(I | undefined)[]>([]);
	const allLoadedItems = computed<I[]>(() => {
		return allItems.value.filter((item) => item !== undefined) as I[];
	});
	const meta = reactive<ListResponseMeta>({
		current_page: 1,
		from: 0,
		last_page: 0,
		path: '',
		per_page: 0,
		to: 0,
		total: 0,
		next_cursor: null,
	});
	const getItemsError = ref<ApiErrorResponse>();
	const hasDynamicTotal = ref(false);
	const currentKey = ref(0);

	const length = computed(() => {
		return meta.total || 0;
	});

	const pageSize = computed<number>(() => {
		return Number(meta.per_page) || params.value.per_page || 0;
	});

	const getItems = async (reset = true) => {
		const currentLocalKey = currentKey.value;
		try {
			isItemsLoading.value = true;
			if (allItems.value) allItems.value.splice(0, allItems.value.length);
			const resp = await service()[requestKey](...args.value, {
				...unref(params),
				page: paginationType !== 'cursor' ? paginationStartPage : undefined,
			});
			if (!resp?.data) return;
			Object.assign(meta, resp.meta);

			// If the total is not provided by the API, we need to update it on
			// the fly based on the number of items returned in each request.
			hasDynamicTotal.value = !Object.hasOwnProperty.call(resp.meta, 'total') || resp.meta.total === null;

			if (hasDynamicTotal.value) {
				if (resp.data.length) {
					meta.total = resp.data.length;

					// An extra item is added to the total to allow for the page provider
					// to be called when the last item is scrolled into view.
					if (resp.data.length >= params.value.per_page) meta.total += 1;
				} else {
					// If no items are returned, the total is set to 0.
					meta.total = 0;
				}
			}

			const allItemsFill = new Array(meta.total).fill(undefined);
			allItems.value = allItems.value.concat(allItemsFill);
			items.value = reset || !items.value ? resp.data : items.value.concat(resp.data);
			getItemsError.value = undefined;
		} catch (error) {
			items.value = reset || !items.value ? [] : items.value.concat([]);
			if (isApiError(error)) {
				console.error(error);
				getItemsError.value = error;
			}
		} finally {
			if (currentLocalKey === currentKey.value) {
				isItemsLoading.value = false;
			}
		}
	};

	const makePageProvider = (providerItems: any): PageProvider => async (pageNumber: number) => {
		if (!providerItems || !providerItems.length) {
			return new Promise<unknown[]>((resolve) => resolve([]));
		}
		if (!pageNumber) {
			return new Promise<unknown[]>((resolve) => {
				allItems.value?.splice(0, providerItems.length, ...providerItems);
				resolve(items.value ?? []);
			});
		}
		return new Promise<unknown[]>((resolve) => {
			if (paginationType === 'cursor' && meta.next_cursor === null) {
				if (hasDynamicTotal.value) meta.total = Math.max(meta.total - 1, 0);
				return resolve([]);
			}

			const page = paginationType === 'index'
				? pageNumber * meta.per_page + paginationStartPage
				: pageNumber + 1;
			const currentLocalKey = currentKey.value;
			service({ key: requestKey, bypassAbort: true })[requestKey](...args.value, {
				...unref(params),
				...paginationType === 'cursor'
					? { cursor: meta.next_cursor }
					: { page },
			}).then((data: GetListResponse) => {
				if (data?.data && currentLocalKey === currentKey.value) {
					const dataCount = data.data.length;
					if (hasDynamicTotal.value) {
						meta.total += dataCount;
						if (dataCount < params.value.per_page) meta.total = Math.max(meta.total - 1, 0);
					}
					meta.next_cursor = data.meta.next_cursor ?? null;
					allItems.value?.splice(pageNumber * meta.per_page, dataCount, ...data.data);
				}
				resolve(data?.data);
			}).catch((error) => {
				console.error(error);
				resolve([]);
			});
		});
	};

	const pageProvider = computed(() => {
		return makePageProvider(items.value);
	});

	const scrollerIndex = ref<number | undefined>();

	const accumulateItemsByScroll = (index: number) => {
		scrollerIndex.value = index;
		nextTick(() => {
			scrollerIndex.value = undefined;
		});
	};

	watch([() => params, () => args], () => {
		currentKey.value += 1;
		getItems();
	}, { deep: true });

	return {
		items,
		allItems,
		allLoadedItems,
		isItemsLoading,
		getItems,
		getItemsError,
		length,
		pageProvider,
		pageSize,
		scrollerIndex,
		accumulateItemsByScroll,
	};
}
